import Papa from 'papaparse'
import { normalizeHeader, isKnownCensusField } from './csv.js'

// Neutral raw shape both PapaParse (csv) and SheetJS (xlsx) converge to:
// headers = the sniffed header row, rows = the data after it, every cell a
// string. Field normalization and validation stay in csv.js so the Import
// Studio and the quick CSV path can never disagree about what a row means; the
// only csv.js knowledge used here is header recognition, to FIND the header row
// inside a messy sheet (title/preamble rows above, totals rows below, header
// text split across stacked rows — the standard advisor-census layout).

const XLSX_EXTENSIONS = /\.(xlsx|xls|xlsm)$/i

export function isSpreadsheetFile(name) {
  return XLSX_EXTENSIONS.test(name || '')
}

export function isCsvFile(name) {
  return /\.(csv|txt|tsv)$/i.test(name || '')
}

const toCellString = (value) => (value === null || value === undefined ? '' : String(value).trim())

// Drop trailing all-blank rows/columns (xlsx sheets routinely carry phantom cells).
function trimMatrix(matrix) {
  const rows = matrix.map((row) => row.map(toCellString))
  while (rows.length && rows[rows.length - 1].every((cell) => cell === '')) rows.pop()
  let width = 0
  for (const row of rows) {
    for (let i = row.length - 1; i >= 0; i -= 1) {
      if (row[i] !== '') {
        width = Math.max(width, i + 1)
        break
      }
    }
  }
  return rows.map((row) => {
    const next = row.slice(0, width)
    while (next.length < width) next.push('')
    return next
  })
}

// Rows after the census that are sheet summaries, not employees: totals,
// deduction estimates, assumption footnotes. Matched on the row's first
// non-empty cell.
const FOOTER_CELL_PATTERN = /^(\*|totals?\b|grand\s+total\b|total\s+payroll\b|considered\s+earnings\b|irc\s*\d|estimate(?:d|s)?(?:\s|:|$)|assumes?(?:\s|:|$)|average\s*:?\s*$|summary\s*:?\s*$)/i

const isFooterRow = (row) => {
  const firstCell = row.find((cell) => cell !== '')
  return firstCell !== undefined && FOOTER_CELL_PATTERN.test(firstCell)
}

// How many distinct known census fields a candidate header row resolves to.
function headerScore(cells) {
  const fields = new Set()
  for (const cell of cells) {
    if (cell === '') continue
    const field = normalizeHeader(cell)
    if (isKnownCensusField(field)) fields.add(field)
  }
  return fields.size
}

// Merge up to `span` stacked rows into one header row per column ("Date" /
// "of" / "Birth" on three rows becomes "Date of Birth").
function mergeHeaderRows(rows) {
  const width = Math.max(...rows.map((row) => row.length))
  return Array.from({ length: width }, (_, column) => (
    rows.map((row) => row[column] || '').filter(Boolean).join(' ').trim()
  ))
}

const HEADER_SCAN_ROWS = 25
const MIN_SNIFF_SCORE = 3

// Split a raw cell matrix into headers + data rows.
//
// Header row: scored sniffing over the first rows — alone and merged with up
// to two rows above (stacked header text) — picking the candidate that
// resolves the most known census fields. A sheet nothing recognizes falls back
// to the first non-empty row, and the Studio lets the user fix it by hand.
// Data rows: everything after the header except blank rows and totals/summary
// footers, which are reported in `notes` rather than silently vanishing.
function matrixToSheet(matrix) {
  const trimmed = trimMatrix(matrix)
  const firstContentIndex = trimmed.findIndex((row) => row.some((cell) => cell !== ''))
  if (firstContentIndex === -1) return { headers: [], rows: [], notes: [] }

  let best = { score: 0, index: firstContentIndex, span: 1, cells: trimmed[firstContentIndex] }
  for (let index = firstContentIndex; index < Math.min(trimmed.length, HEADER_SCAN_ROWS); index += 1) {
    for (let span = 1; span <= 3 && index - span + 1 >= 0; span += 1) {
      const cells = span === 1
        ? trimmed[index]
        : mergeHeaderRows(trimmed.slice(index - span + 1, index + 1))
      const score = headerScore(cells)
      // Strictly-better wins, so ties keep the earliest, least-merged candidate.
      if (score > best.score) best = { score, index, span, cells }
    }
  }
  if (best.score < MIN_SNIFF_SCORE) {
    best = { score: 0, index: firstContentIndex, span: 1, cells: trimmed[firstContentIndex] }
  }

  const notes = []
  if (best.index > firstContentIndex) {
    notes.push(`Skipped ${best.index - firstContentIndex} title row${best.index - firstContentIndex === 1 ? '' : 's'} above the column headers.`)
  }
  const headers = best.cells.map((cell, i) => cell || `Column ${i + 1}`)

  // Salvage labels stranded in the skipped title rows. TPA sheets often park a
  // column's real name (e.g. "SH 3%NEC", "ER P/S") in a banner row above the
  // main header block, so the header cell itself is blank and the column comes
  // through as "Column N". Capturing that text – nearest-to-header first – lets
  // the import review tell the user what an otherwise nameless column was.
  const headerTop = Math.max(firstContentIndex, best.index - (best.span || 1) + 1)
  const columnHints = headers.map(() => '')
  if (headerTop > firstContentIndex) {
    const titleRows = trimmed.slice(firstContentIndex, headerTop)
    for (let col = 0; col < headers.length; col += 1) {
      const parts = []
      for (let r = titleRows.length - 1; r >= 0; r -= 1) {
        const cell = (titleRows[r][col] || '').trim()
        // Skip long banner sentences ("For the Plan Year January 1, ..."); a
        // real column label is short.
        if (cell && cell.length <= 24 && !parts.includes(cell)) parts.push(cell)
      }
      columnHints[col] = parts.join(' ').slice(0, 48)
    }
  }
  const candidateRows = trimmed.slice(best.index + 1).filter((row) => row.some((cell) => cell !== ''))
  const rows = candidateRows.filter((row) => !isFooterRow(row))
  const footerCount = candidateRows.length - rows.length
  if (footerCount > 0) {
    const samples = candidateRows.filter(isFooterRow).slice(0, 2)
      .map((row) => `“${row.find((cell) => cell !== '')}”`).join(', ')
    notes.push(`Skipped ${footerCount} totals/summary row${footerCount === 1 ? '' : 's'} (${samples}).`)
  }
  return { headers, rows, notes, columnHints }
}

export const __matrixToSheetForTests = matrixToSheet

function parseCsvMatrix(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (results) => resolve(results.data),
      error: reject,
    })
  })
}

async function parseXlsxWorkbook(file) {
  // Dynamic import keeps SheetJS (~400kb) out of the main bundle; only Import
  // Studio uploads pay for it.
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(await file.arrayBuffer(), { cellDates: true, dense: true })
  const sheets = []
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name]
    if (!sheet) continue
    const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: false })
    const parsed = matrixToSheet(matrix)
    if (parsed.rows.length > 0 || parsed.headers.length > 0) sheets.push({ name, ...parsed })
  }
  return sheets
}

// parseSpreadsheetFile(file, { sheetName }) =>
//   { headers: string[], rows: string[][], sheetNames: string[], sheetName, warnings: string[] }
export async function parseSpreadsheetFile(file, { sheetName } = {}) {
  const warnings = []

  if (isSpreadsheetFile(file.name)) {
    const sheets = await parseXlsxWorkbook(file)
    if (sheets.length === 0) throw new Error('No data found in this workbook.')
    const active = (sheetName && sheets.find((s) => s.name === sheetName)) || sheets[0]
    if (sheets.length > 1 && !sheetName) {
      warnings.push(`This workbook has ${sheets.length} sheets with data – showing “${active.name}”.`)
    }
    warnings.push(...(active.notes || []))
    return {
      headers: active.headers,
      rows: active.rows,
      columnHints: active.columnHints || [],
      sheetNames: sheets.map((s) => s.name),
      sheetName: active.name,
      warnings,
    }
  }

  const matrix = await parseCsvMatrix(file)
  const { headers, rows, notes, columnHints } = matrixToSheet(matrix)
  if (headers.length === 0) throw new Error('No data found in this file.')
  warnings.push(...(notes || []))
  return { headers, rows, columnHints: columnHints || [], sheetNames: [], sheetName: null, warnings }
}
